import { Company } from '../../auth/models/company/company';
import { User } from '../../auth/models/user/user';
import { environment } from '../../../environments/environment';

/**
 * The one place the v2 navigation is defined. The topbar, the sidebar, the
 * mobile drawer and the jump-search all render from this array.
 *
 * `built: false` means the page has not been rebuilt in v2 yet: it renders
 * muted, is marked "classic", and its `route` points at the legacy path.
 * Promoting a page is a two-word change — flip `built`, repoint `route`.
 */
export interface V2NavItem {
    label: string;
    icon: string;                                        // lucide icon name
    route: string;                                       // '/v2/…' when built, legacy '/…' when not
    built: boolean;
    visible: (user: User, company: Company) => boolean;
    /** A live count shown beside the label in words ("4 to sign"); the bar reads it. */
    count?: 'supplierAwardsToSign';
}

export interface V2NavGroup {
    title: string;
    visible: (user: User, company: Company) => boolean;
    items: V2NavItem[];
}

/**
 * The one call-to-action the global bar carries to the right of the sections.
 * Separate from `V2NavItem` because it is not navigation: it has no icon, no
 * `built` flag and no route — the topbar decides what pressing it does.
 */
export interface V2NavAction {
    label: string;
    testId: string;

    /**
     * True when pressing this lands the user back in the legacy UI, so the bar
     * can mark it the same way it marks an unbuilt nav item. The bar's rule is
     * that anything leaving v2 says so before you click it, and the action is
     * not exempt.
     */
    leavesV2: boolean;

    visible: (user: User, company: Company) => boolean;
}

export const V2_LANDING_ROUTE = '/v2/tenders';

/** Where a supplier lands in v2 (supplier v2 Plan 1, spec D1). */
export const V2_SUPPLIER_LANDING_ROUTE = '/v2/supplier/tenders';
/** The supplier's Awards across airlines and Reports › Contracts (supplier v2 Plan 6). */
export const V2_SUPPLIER_AWARDS_ROUTE = '/v2/supplier/awards';
export const V2_SUPPLIER_CONTRACTS_ROUTE = '/v2/supplier/reports/contracts';
/** The supplier's own airline customers (premium supplier feature, open to all in beta). */
export const V2_SUPPLIER_CUSTOMERS_ROUTE = '/v2/supplier/customers';

/** Where a No Access person lands: the settings hub (spec section 3). */
export const V2_SETTINGS_LANDING_ROUTE = '/v2/settings';

/** The in-app tutorials hub, for both personas (tutorials/tutorials.routes.ts). Help links here too. */
export const V2_TUTORIALS_ROUTE = '/v2/tutorials';

/** Where /v2 starts for this person: Settings for No Access, else the supplier or airline list. */
export function v2LandingFor(company: Company, user?: User): string {
    if (user?.isNoAccess()) {
        return V2_SETTINGS_LANDING_ROUTE;
    }
    return company.isSupplier() ? V2_SUPPLIER_LANDING_ROUTE : V2_LANDING_ROUTE;
}

/**
 * Both Contacts endpoints are `CanManage` (`supplier_viewset.py:26,45`) — a
 * Viewer must never see the page. This is the ONE place that rule lives: the
 * nav item's `visible` below and the `contacts` route's `canMatch` guard
 * (`v2/guards/v2-contacts.guard.ts`) both call this, rather than each
 * carrying its own copy of "who can see Contacts" that could drift apart.
 */
export function v2CanManageContacts(user: User): boolean {
    return user.canManage();
}

/**
 * Year over year needs the user's own `view_reports` setting (classic CanViewReportsGuard;
 * the server's HasViewReports). One rule for the Reports ▾ item, the report sub-tab lock
 * (shared/report-tabs/report-locks.ts) and the page's locked state (pages/year-over-year, V10).
 */
export function v2CanViewReports(user: { settings?: { view_reports?: boolean } | null } | null | undefined): boolean {
    return !!user?.settings?.view_reports;
}

/**
 * The location dashboard's route pattern, relative to the /v2 root, and the
 * builder that turns it into a URL.
 *
 * Both live here rather than as a literal in `v2.routes.ts` because two places
 * need the same fact: the router needs the pattern and the breadcrumb needs
 * concrete URLs built from it. This project has been bitten five times by a
 * fact with no single source of truth being copied and the copies rotting, so
 * the segments are written down once.
 *
 * The segments are the legacy ones verbatim (`tenders/:id/locations/:id/
 * dashboard/round/:rid`), except that the location parameter is `:lid`: a flat
 * route cannot use `:id` twice.
 *
 * NONE OF THE THREE IS WHAT ITS NAME SUGGESTS. Every one of them was read back
 * off the API the legacy resolvers call, because guessing here produces a page
 * that 404s its own data while the route itself looks fine:
 *
 *   `:id`   the tender's **uid** — the six-character base26 code (`VE5A3C`),
 *           not its primary key. Every buyer endpoint captures this segment as
 *           `uid` and looks the tender up with `get_comp_tender(uid, company)`
 *           (`backend/jetfueltenders/buyer/urls.py`).
 *   `:lid`  a **Contract** primary key, despite the `locations` segment. A
 *           Contract is one location's line on one tender, and that is the row
 *           the dashboard is about: `TenderContractResolver` feeds this to
 *           `GET /v2/buyer/tenders/:uid/contracts/:lid/`.
 *   `:rid`  the round **number** — 1, 2, 3 — not the Round primary key. The
 *           suppliers endpoint captures it as `round_num` and filters
 *           `Round.objects.filter(round=round_num, contract=contract)`, and
 *           legacy reads it into a field it calls `roundNum`
 *           (`tender-contract-dashboard.component.ts`).
 */
export const V2_ROUND_DASHBOARD_PATH = 'tenders/:id/locations/:lid/dashboard/round/:rid';

export function v2RoundDashboardUrl(
    tenderUid: string,
    contractId: number | string,
    roundNumber: number | string
): string {
    return `/v2/tenders/${tenderUid}/locations/${contractId}/dashboard/round/${roundNumber}`;
}

export const v2NavGroups: V2NavGroup[] = [
    {
        title: 'Tenders',
        visible: (user, company) => company.isBuyer() && !user.isNoAccess(),
        items: [
            {
                label: 'Tenders',
                icon: 'lucideFileText',
                route: '/v2/tenders',
                built: true,
                visible: () => true
            },
            {
                label: 'Awarded Contracts',
                icon: 'lucideFileCheck',
                route: '/v2/awarded-contracts',
                built: true,
                // Every buyer role reads it (IsBuyer, Viewer included; awarded_contracts.py).
                visible: () => true
            }
        ]
    },
    {
        title: 'Directory',
        visible: (user, company) => company.isBuyer() && !user.isNoAccess(),
        items: [
            {
                label: 'Contacts',
                icon: 'lucideUsers',
                route: '/v2/contacts',
                built: true,
                // Both contacts endpoints are CanManage
                // (backend/jetfueltenders/buyer/views/supplier_viewset.py:26, :45),
                // which is canManage() minus Viewer. The route itself is also
                // guarded (v2ContactsGuard, same rule) so typing the URL
                // cannot reach the page and its two 403s either.
                visible: (user) => v2CanManageContacts(user)
            }
        ]
    },
    {
        // Supplier v2 (Plan 1): only what is built. Plan 6: Awards (with the awards to sign) and
        // Reports › Contracts. Supply Locations is its v2 page (it opened classic until 2026-10-06).
        title: 'Tenders',
        visible: (user, company) => company.isSupplier() && !user.isNoAccess(),
        items: [
            {
                label: 'Tenders',
                icon: 'lucideFileText',
                route: V2_SUPPLIER_LANDING_ROUTE,
                built: true,
                visible: () => true
            },
            {
                label: 'Awards',
                icon: 'lucideFileCheck',
                route: V2_SUPPLIER_AWARDS_ROUTE,
                built: true,
                visible: () => true,
                count: 'supplierAwardsToSign'
            },
            {
                // Customers (brief 2026-10-10): Company.hasSupplierPremium: the extras switch on (D-420)
                // and, with the paywall on, the plan. A plan that lapsed reaches it by link and reads only (Q6).
                label: 'Customers',
                icon: 'lucideUsers',
                route: V2_SUPPLIER_CUSTOMERS_ROUTE,
                built: true,
                visible: (_user, company) => company.hasSupplierPremium()
            },
            {
                // Customers › Customer Tenders (brief 2026-10-10 Q4), next to Customers; every supplier
                // whose extras switch is on (D-420; the plan counts only for creating, Q1).
                label: 'Customer Tenders',
                icon: 'lucideUsers',  // already registered by the bar and the drawer
                route: '/v2/supplier/customer-tenders',
                built: true,
                visible: (_user, company) => company.hasSupplierExtras()
            },
            {
                label: 'Supply Locations',
                icon: 'lucideMapPin',
                route: '/v2/supplier/supply-locations',
                built: true,
                visible: () => true
            }
        ]
    }
    // No "Account"/"Settings" group here (Controller ruling 6): the spec's
    // navigation does not carry one, and the account menu's "Settings"
    // (/v2/settings) is the only way in — a bar item and a menu item pointing
    // at the same page would be two sources of truth for one link.
];

export function visibleGroups(user: User, company: Company): V2NavGroup[] {
    // Required Before Using the Platform (D-610): no section is open while the company may only sign.
    if (user.isDocumentBlocked?.()) {
        return [];
    }
    return v2NavGroups
        .filter(group => group.visible(user, company))
        .map(group => ({
            ...group,
            items: group.items.filter(item => item.visible(user, company))
        }))
        .filter(group => group.items.length > 0);
}

/**
 * The global bar's Reports ▾ menu — the supplier's only (supplier v2 D11, Plan 6): Contracts,
 * for every role (D31), no tender needed.
 *
 * The airline has no Reports menu (Anton, 2026-10-10): its four reports (Bid Analysis, Bid Fee,
 * Award Scenarios, Year over Year) are the tender's own Reports tab (TENDER_PAGES in v2-crumbs.ts),
 * and Index Analytics is a tab of the location. Every item here is gated by `visible`, so an
 * airline's menu comes out empty and the bar draws none. No Access gets none either.
 */
export function v2ReportsItems(user?: User): V2NavItem[] {
    if (user?.isNoAccess()) {
        return [];
    }

    return [{
        label: 'Contracts',
        icon: 'lucideFileText',
        route: V2_SUPPLIER_CONTRACTS_ROUTE,
        built: true,
        visible: (_user, company) => company.isSupplier()
    }];
}

/**
 * "New tender" — the only primary action on the v2 bar.
 *
 * The gate is copied verbatim from the legacy navbar, where it is spread over
 * two nested `*ngIf`s (`navbar.component.html:86-88`):
 *
 *     <li  *ngIf="company.isBuyer() && user.canManage()">
 *       <button *ngIf="!company.isChild"> <create-tender-button> </button>
 *
 * so the effective condition is the conjunction of the three. `canManage()` is
 * true for User, Manager and Admin and false for a Viewer; `isChild` is the
 * real property name on the Company model (mapped from `is_child`), and a child
 * airline may not create tenders at all.
 */
export const v2PrimaryAction: V2NavAction = {
    label: 'New Tender',
    testId: 'v2-new-tender',

    // Posts a draft and opens the v2 wizard at /v2/tenders/new/:id/details
    // (Plan 8 T13). The classic /tenders/new/… stays reachable by URL.
    leavesV2: false,

    visible: (user, company) => company.isBuyer() && user.canManage() && !company.isChild && !user.isNoAccess()
};

/**
 * Whether /v2 has anything to offer this person.
 *
 * Only the buyer pages have been rebuilt, and `isSuperAdmin()` is a JFT staff role,
 * never an airline one — an admin's whole workspace lives under /admin. Either
 * of those personas would get a bar of pure "classic" links, so the guard sends
 * them back to their legacy landing route instead.
 */
export function v2SupportsPersona(user: User, company: Company, supplierV2 = environment.supplierV2): boolean {
    if (user.isSuperAdmin()) {
        return false;
    }
    // Suppliers come in once the build carries supplier v2 (Plan 1 Release decisions:
    // off in production builds until Plans 1-2 are done).
    return company.isBuyer() || (supplierV2 && company.isSupplier());
}

/**
 * Where a person belongs in the legacy UI. Each of these three routes is
 * registered: '/admin-v2/overview' (the admin v2 home, `admin-v2/admin-v2.routes.ts`;
 * the old admin is retired), '/supplier/tenders' (`supplier/routing/routes/tenders`, the
 * 'supplier' parent's redirect target), and '/tenders'
 * (`buyer/routing/routes/tenders`, mounted at the root by BuyerModule).
 *
 * The admin check comes first deliberately: an admin's company is whatever they
 * are currently impersonating, so the company type must not decide for them.
 */
export function v2LegacyLandingFor(user: User, company: Company): string {
    if (user.isSuperAdmin()) {
        return '/admin-v2/overview';
    }

    if (company.isSupplier()) {
        return '/supplier/tenders';
    }

    return '/tenders';
}
